import { useMemo } from 'react';
import { ForecastModel } from '@app/common/static-data/models';
import { useTimezone } from '@app/contexts/useTimezone';
import { PlottableForecast, ModelId } from '../types';
import {
  groupForecastIntoTimeSlots,
  filterSlotsByTargetHours,
  calculateSlotStatistics,
  groupSlotsByDay,
  getPredominantCondition,
  hasPrecipitation,
  WeatherSummarySlot,
  TimeSlotConfig,
} from '../utils/weatherSummaryUtils';

/**
 * Configuration options for the weather summary hook
 */
export interface UseWeatherSummaryConfig {
  /** Time slot configuration */
  timeSlotConfig?: TimeSlotConfig;
  /** Whether to filter to specific target hours (e.g., 6AM, 9AM, 12PM, etc.) */
  useTargetHours?: boolean;
  /** Target hours to show if useTargetHours is true */
  targetHours?: number[];
  /** Whether to group slots by day */
  groupByDay?: boolean;
}

/**
 * Return type for the weather summary hook
 */
export interface WeatherSummaryResult {
  /** Array of weather summary slots */
  slots: WeatherSummarySlot[];
  /** Slots grouped by day (YYYY-MM-DD) if groupByDay is enabled */
  slotsByDay: Record<string, WeatherSummarySlot[]> | null;
  /** Statistical summary across all slots */
  statistics: ReturnType<typeof calculateSlotStatistics>;
  /** The predominant weather condition across all slots */
  predominantCondition: string;
  /** Whether any precipitation is expected */
  hasPrecipitation: boolean;
  /** Whether data is available */
  hasData: boolean;
  /** The model ID used for this summary */
  modelId: ModelId | null;
}

/**
 * Custom hook to process forecast data into weather summary slots
 *
 * @param forecastData - Record of forecast data by model ID
 * @param selectedModels - Array of selected models
 * @param config - Configuration options
 * @returns Weather summary result with processed slots and statistics
 *
 * @example
 * ```typescript
 * const { slots, statistics, hasData } = useWeatherSummary(
 *   forecastData,
 *   selectedModels,
 *   { timeSlotConfig: { intervalHours: 3, maxSlots: 8 } }
 * );
 *
 * if (hasData) {
 *   slots.forEach(slot => {
 *     console.log(slot.time, slot.weatherState.condition, slot.weatherState.temperature);
 *   });
 * }
 * ```
 */
export function useWeatherSummary(
  forecastData: Record<ModelId, PlottableForecast>,
  selectedModels: ForecastModel[],
  config: UseWeatherSummaryConfig = {},
): WeatherSummaryResult {
  const { formatInUserTimezone } = useTimezone();

  const {
    timeSlotConfig = { intervalHours: 3, maxSlots: 8 },
    useTargetHours = false,
    targetHours = [6, 9, 12, 15, 18, 21],
    groupByDay = false,
  } = config;

  // Memoize the result to avoid unnecessary recalculations
  const result = useMemo(() => {
    // Determine which model to use for the summary
    // Strategy: Use the first selected model that has forecast data
    let selectedModelId: ModelId | null = null;
    let selectedForecast: PlottableForecast | null = null;

    for (const model of selectedModels) {
      if (forecastData[model]) {
        selectedModelId = model;
        selectedForecast = forecastData[model];
        break;
      }
    }

    // If no forecast data is available, return empty result
    if (!selectedForecast || !selectedModelId) {
      return {
        slots: [],
        slotsByDay: null,
        statistics: null,
        predominantCondition: 'clear',
        hasPrecipitation: false,
        hasData: false,
        modelId: null,
      };
    }

    // Create function to get local hour from a date
    const getLocalHour = (date: Date): number => {
      const hourStr = formatInUserTimezone(date, 'H'); // 'H' gives 0-23 hour
      return parseInt(hourStr, 10);
    };

    // Group forecast into time slots with timezone conversion
    const configWithTz: TimeSlotConfig = {
      ...timeSlotConfig,
      getLocalHour,
    };
    let slots = groupForecastIntoTimeSlots(selectedForecast, configWithTz);

    // Apply target hours filter if enabled
    if (useTargetHours && slots.length > 0) {
      slots = filterSlotsByTargetHours(slots, targetHours);
    }

    // Group by day if requested
    const slotsByDay = groupByDay ? groupSlotsByDay(slots) : null;

    // Calculate statistics
    const statistics = calculateSlotStatistics(slots);

    // Determine predominant condition
    const predominantCondition = getPredominantCondition(slots);

    // Check for precipitation
    const hasRain = hasPrecipitation(slots);

    return {
      slots,
      slotsByDay,
      statistics,
      predominantCondition,
      hasPrecipitation: hasRain,
      hasData: slots.length > 0,
      modelId: selectedModelId,
    };
  }, [
    forecastData,
    selectedModels,
    timeSlotConfig,
    useTargetHours,
    targetHours,
    groupByDay,
    formatInUserTimezone,
  ]);

  return result;
}

/**
 * Hook variant that processes weather summary for multiple models
 * Returns a map of model ID to weather summary result
 *
 * @param forecastData - Record of forecast data by model ID
 * @param selectedModels - Array of selected models
 * @param config - Configuration options
 * @returns Map of model ID to weather summary result
 */
export function useMultiModelWeatherSummary(
  forecastData: Record<ModelId, PlottableForecast>,
  selectedModels: ForecastModel[],
  config: UseWeatherSummaryConfig = {},
): Record<ModelId, WeatherSummaryResult> {
  const { formatInUserTimezone } = useTimezone();

  const results = useMemo(() => {
    const summaries: Record<string, WeatherSummaryResult> = {};

    // Create function to get local hour from a date
    const getLocalHour = (date: Date): number => {
      const hourStr = formatInUserTimezone(date, 'H'); // 'H' gives 0-23 hour
      return parseInt(hourStr, 10);
    };

    selectedModels.forEach((model) => {
      const forecast = forecastData[model];
      if (!forecast) {
        summaries[model] = {
          slots: [],
          slotsByDay: null,
          statistics: null,
          predominantCondition: 'clear',
          hasPrecipitation: false,
          hasData: false,
          modelId: model,
        };
        return;
      }

      // Process this model's forecast
      const {
        timeSlotConfig = { intervalHours: 3, maxSlots: 8 },
        useTargetHours = false,
        targetHours = [6, 9, 12, 15, 18, 21],
        groupByDay = false,
      } = config;

      // Add function to get local hour to config
      const configWithTz: TimeSlotConfig = {
        ...timeSlotConfig,
        getLocalHour,
      };
      let slots = groupForecastIntoTimeSlots(forecast, configWithTz);

      if (useTargetHours && slots.length > 0) {
        slots = filterSlotsByTargetHours(slots, targetHours);
      }

      const slotsByDay = groupByDay ? groupSlotsByDay(slots) : null;
      const statistics = calculateSlotStatistics(slots);
      const predominantCondition = getPredominantCondition(slots);
      const hasRain = hasPrecipitation(slots);

      summaries[model] = {
        slots,
        slotsByDay,
        statistics,
        predominantCondition,
        hasPrecipitation: hasRain,
        hasData: slots.length > 0,
        modelId: model,
      };
    });

    return summaries;
  }, [forecastData, selectedModels, config, formatInUserTimezone]);

  return results;
}
